import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { User } from '@/types';
import { authApi } from '@services/apiService';
import { useNotificationStore } from './notificationStore';

interface AuthState {
  // 状态
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // 方法
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,

        // 设置用户
        setUser: (user) => {
          set({ user, isAuthenticated: !!user });
        },

        // 设置 token
        setToken: (token) => {
          set({ token });
        },

        // 登录
        login: (user, token) => {
          set({ user, token, isAuthenticated: true });

          // 存储 token 到 localStorage
          localStorage.setItem('token', token);

          // 登录成功后连接 WebSocket
          if (token) {
            useNotificationStore.getState().connect(token);
          }
        },

        // 登出
        logout: () => {
          // 登出前断开 WebSocket 连接
          useNotificationStore.getState().disconnect();
          // 从 localStorage 移除 token
          localStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false });
        },

        // 检查认证状态
        checkAuth: async () => {
          const { token } = get();
          if (!token) {
            set({ isAuthenticated: false });
            return;
          }

          set({ isLoading: true });
          try {
            // 调用 API 验证 token
            const user = await authApi.verifyToken();
            set({ user, isAuthenticated: true, isLoading: false });
          } catch (error) {
            // Token 无效或过期，清除状态
            localStorage.removeItem('token');
            set({ user: null, token: null, isAuthenticated: false, isLoading: false });
          }
        },

        // 更新用户信息
        updateUser: (userData) => {
          const { user } = get();
          if (user) {
            set({ user: { ...user, ...userData } });
          }
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({ token: state.token }),
      }
    ),
    { name: 'AuthStore' }
  )
);
